import {
  INDUSTRY__BUILDING,
  INDUSTRY__ENERGY_AND_WATER_SUPPLY,
  INDUSTRY__MANUFACTURING,
  INDUSTRY__MINING,
  INDUSTRY__SERVICE,
  INDUSTRY__FINANCE_AND_INSURANCE,
  INDUSTRY__HOSPITALITY,
  INDUSTRY__HEALTH_AND_SOCIAL,
  INDUSTRY__TRADE,
  INDUSTRY__AGRICULTURE,
  INDUSTRY__CRAFT,
  INDUSTRY__OTHER,
  INDUSTRY__PROCESSING,
  INDUSTRY__REAL_ESTATE,
  INDUSTRY__TRANSPORT,
  INDUSTRY__MAINTENANCE,
  INDUSTRY__SOCIAL,
  INDUSTRY__HEALTH,
  INDUSTRY__RESIDE,
  INDUSTRY__FUNDRAISER,
  INDUSTRY__FOUNDATION,
  INDUSTRY__RELIGIOUS,
  INDUSTRY__UPBRINGING,
  INDUSTRY__OTHER_SOCIAL_HEALTH,
  INDUSTRY__OTHER_NON_SOCIAL_HEALTH,
  INDUSTRY__PRIVATE_PERSON,
} from 'modules/Inquiry/Form/formFields';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { chooseSelectedInquiryTypeSpecificValue } from 'shared/chooseSelectedInquiryTypeSpecificComponent';
import { useTranslations } from 'utils/hooks/useTranslations';

export const getTranslationPath = (id) =>
  `pages.userProfile.sections.completingCompanyDetails.fields.userProfileIndustry.selectOptions.${id}`;

/*
 * The unified SozialBank Branche list (ECBFS-4), in the order the dropdown offers it.
 * These keys are the backend's CUSTOM_INDUSTRY_VALUES — anything outside this set is
 * rejected with a 422 on submit. The labels are synced verbatim to CleverReach, so the
 * `selectOptions` copy has to stay character-identical to the backend's
 * clever_reach.de.yml wording.
 */
export const bfsIndustryOptions = {
  [INDUSTRY__SOCIAL]: getTranslationPath(INDUSTRY__SOCIAL),
  [INDUSTRY__HEALTH]: getTranslationPath(INDUSTRY__HEALTH),
  [INDUSTRY__UPBRINGING]: getTranslationPath(INDUSTRY__UPBRINGING),
  [INDUSTRY__OTHER_SOCIAL_HEALTH]: getTranslationPath(INDUSTRY__OTHER_SOCIAL_HEALTH),
  [INDUSTRY__OTHER_NON_SOCIAL_HEALTH]: getTranslationPath(INDUSTRY__OTHER_NON_SOCIAL_HEALTH),
  [INDUSTRY__PRIVATE_PERSON]: getTranslationPath(INDUSTRY__PRIVATE_PERSON),
};

/*
 * Branche options retired by ECBFS-4. They are no longer selectable, but companies
 * created before the change still carry these values, so they stay translatable for the
 * summary table and the portal detail pages. Do not add them back to bfsIndustryOptions.
 */
export const retiredBfsIndustryOptions = {
  [INDUSTRY__MAINTENANCE]: getTranslationPath(INDUSTRY__MAINTENANCE),
  [INDUSTRY__RESIDE]: getTranslationPath(INDUSTRY__RESIDE),
  [INDUSTRY__FUNDRAISER]: getTranslationPath(INDUSTRY__FUNDRAISER),
  [INDUSTRY__FOUNDATION]: getTranslationPath(INDUSTRY__FOUNDATION),
  [INDUSTRY__RELIGIOUS]: getTranslationPath(INDUSTRY__RELIGIOUS),
  [INDUSTRY__OTHER]: getTranslationPath(INDUSTRY__OTHER),
};

export const profileIndustryOptions = {
  [INDUSTRY__BUILDING]: getTranslationPath('building'),
  [INDUSTRY__MINING]: getTranslationPath('mining'),
  [INDUSTRY__ENERGY_AND_WATER_SUPPLY]: getTranslationPath('energy_and_water_supply'),
  [INDUSTRY__SERVICE]: getTranslationPath('service'),
  [INDUSTRY__FINANCE_AND_INSURANCE]: getTranslationPath('finance_and_insurance'),
  [INDUSTRY__HOSPITALITY]: getTranslationPath('hospitality'),
  [INDUSTRY__HEALTH_AND_SOCIAL]: getTranslationPath('health_and_social'),
  [INDUSTRY__TRADE]: getTranslationPath('trade'),
  [INDUSTRY__CRAFT]: getTranslationPath('craft'),
  [INDUSTRY__REAL_ESTATE]: getTranslationPath('real_estate'),
  [INDUSTRY__AGRICULTURE]: getTranslationPath('agriculture'),
  [INDUSTRY__MANUFACTURING]: getTranslationPath('manufacturing'),
  [INDUSTRY__PROCESSING]: getTranslationPath('processing'),
  [INDUSTRY__TRANSPORT]: getTranslationPath('transport'),
  [INDUSTRY__OTHER]: getTranslationPath('other'),
};

/*
 * Every industry value that can reach the frontend, including the retired BfS ones, so a
 * company created before ECBFS-4 still renders a label instead of its raw key.
 */
const allIndustryOptions = {
  ...profileIndustryOptions,
  ...bfsIndustryOptions,
  ...retiredBfsIndustryOptions,
};

export const getSelectTranslations = (formatMessage) => {
  const t = (id) => id && formatMessage({ id });

  return Object.entries(allIndustryOptions).reduce(
    (accumulator, [value, id]) => ({ ...accumulator, [value]: t(id) }),
    {},
  );
};

export const getSelectValues = (formatMessage) => {
  const translations = getSelectTranslations(formatMessage);

  return Object.keys(translations).map((key) => ({
    label: translations[key],
    value: key,
  }));
};

// Values from DKB
export const MUTLISELECT_VALUES = {
  Ackerbau: 'Ackerbau',
  Milcherzeugung: 'Milcherzeugung',
  Rindermast: 'Rindermast',
  Schweinehaltung: 'Schweinehaltung',
  Geflügelhaltung: 'Geflügelhaltung',
  Biogas: 'Biogas',
  Sonderkulturen: 'Sonderkulturen',
  'Lohn - Dienstleistungen': 'Lohn - Dienstleistungen',
  Handel: 'Handel',
  Sonstiges: 'Sonstiges',
};

export const useMultiSelectValues = () => {
  const t = useTranslations();
  return chooseSelectedInquiryTypeSpecificValue({
    [InquiryType.bfs]: Object.entries(bfsIndustryOptions).map(([key, val]) => ({
      label: t(val),
      value: key,
    })),
    default: () => Object.keys(MUTLISELECT_VALUES).map((key) => ({ label: key, value: key })),
  });
};
